// sim/castes.ts — caste BEHAVIOUR TEMPLATES (the soul of the colony layer).
//
// A caste is not a subclass and not just a stat block — it is a deterministic,
// allocation-free behaviour, expressed once as a stateless singleton and SHARED
// by every member of that caste. A colony of 10,000 Workers holds ONE
// WorkerBehavior. Dispatch is a fixed lookup by caste id (CASTE_BEHAVIORS), so
// the per-tick loop never allocates a closure or object per organism.
//
//   Worker     gather food → carry it home → deposit into the colony store
//   Soldier    patrol near home; chase & strike rival-colony organisms
//   Flyer      scout the map (ignoring walls/crowds); refuel at home when low
//   Specialist dormant until the colony is food-stressed, then forage; stand
//              down (idle) once recovered
//
// Shared rule (applied centrally in organisms.ts, not here): an organism on or
// next to its nest is fed back to full energy — members must stay tethered to
// the colony or starve out in the field.

import { DIR, DIR_DX, DIR_DY, DIR_COUNT, NIL, CASTE, ORGANISM_STATE } from '../types.ts';
import type { EntityId, Caste, Tick, GeneConfig, MutationEnergySink } from '../types.ts';
import type { EntitySystem } from '../engine/EntitySystem.ts';
import type { WorldGrid } from '../engine/WorldGrid.ts';
import type { Rng } from '../rng.ts';
import type { ColonySystem } from './colony.ts';
import type { ResourceSystem } from './resources.ts';
import type { Pathing } from '../types.ts';
import { chebyshev } from '../engine/Pathing.ts';
import { senseNearestFood, senseNearestEnemy, senseNearestWildlife } from './sensing.ts';
import type { GeneInventory } from './genes.ts';
import { rollCombatDrop, rollDeathDrop } from './geneForge.ts';
import type { GenomeSystem, CasteProfile } from './genome.ts';
import { SPEED_UNIT } from './genome.ts';
import type { EcosystemModifiers } from './ecosystemEvents.ts';

/**
 * Everything a behaviour needs, assembled ONCE per world and passed by reference
 * to every update call. Never rebuilt per tick or per organism.
 */
export interface OrganismCtx {
  readonly entities: EntitySystem;
  readonly grid: WorldGrid;
  readonly rng: Rng;
  readonly colonies: ColonySystem;
  readonly resources: ResourceSystem;
  /** Resolves each organism's captured genome profile (its genetics). */
  readonly genomes: GenomeSystem;
  /**
   * Ground pathing (respects walls + the per-tile cap). The default everywhere is
   * GreedyPathing; the field is the `Pathing` INTERFACE so a world may inject an
   * A* (e.g. for soldiers around walls) without changing this contract.
   */
  readonly pathing: Pathing;
  /**
   * OPTIONAL danger-aware ground pathing used ONLY for the FORAGING + return legs
   * of the ground forage castes (Worker / Specialist). When set (the opt-in
   * `dangerAvoidance` ecosystem layer wires a weighted, bounded A* with a predator
   * cost field), foragers route AROUND predator territory instead of straight
   * through it. ABSENT ⇒ foragers use `pathing` (greedy) exactly as before, so the
   * world is byte-identical. Soldiers, task pursuit, and flyers always use
   * `pathing` — soldiers actively HUNT wildlife, so they must not flee it.
   */
  readonly foragePathing?: Pathing | undefined;
  /** Live tick reference (set by OrganismSystem.update) so kills stamp loot. */
  readonly clock: { tick: Tick };
  /** Gene store (optional — gene drops are opt-in for a world). */
  readonly genes?: GeneInventory;
  /** Gene-loot tuning (optional; paired with `genes`). */
  readonly geneConfig?: GeneConfig;
  /**
   * Mutation-energy sink (optional). When wired, a kill awards energy to the
   * KILLER's colony via the zero-dep MutationEnergySink seam. Unwired ⇒ kills
   * are byte-identical to today.
   */
  readonly mutation?: MutationEnergySink;
  /**
   * Active ecosystem-event modifiers (optional — events are opt-in). Absent ⇒
   * neutral, so a world without events is byte-identical: the organism loop reads
   * `organismDrain` and gene-drop rolls read `geneDropPct`, both no-ops at neutral.
   */
  readonly eventMods?: EcosystemModifiers | undefined;
  /**
   * Per-colony gene-drop-rate percent from completed GeneDropRate region projects
   * (the "cultivated mutagens" bonus), keyed by the RECIPIENT colony of a drop.
   * Returns 100 for a colony with no such project. Optional — absent ⇒ neutral 100,
   * so a world that doesn't wire it is byte-identical (and the roll still draws
   * exactly one rng.int(100) either way, so the stream never shifts).
   */
  readonly projectGeneDropPctOf?: (cid: number) => number;
  /**
   * PER-CREATURE MOVEMENT SPEED (opt-in). When true, `advance` spends each organism's
   * fixed-point `p.speed` through a sub-tick accumulator (`entities.moveAccum`) so a
   * creature can move SUB-tile (slow tank) or smoothly fast — a real speed STAT.
   * ABSENT / false ⇒ the ORIGINAL integer `stepsPerTick` loop runs, untouched, so the
   * world is BYTE-IDENTICAL to before this feature existed (the determinism gate). Only
   * `advance` branches on it; `stepOnce`/`wander` and every other mover are unchanged.
   */
  readonly creatureSpeed?: boolean;
}

/** This organism's resolved genome profile (captured at spawn via profileRef). */
function profileOf(ctx: OrganismCtx, id: EntityId): CasteProfile {
  return ctx.genomes.profile(ctx.entities.profileRef[id] as number);
}

/** A caste's behaviour template: stateless, deterministic, allocation-free. */
export interface CasteBehavior {
  readonly caste: Caste;
  readonly name: string;
  /** Advance organism `id` by one tick. Must not allocate. */
  update(ctx: OrganismCtx, id: EntityId): void;
}

// ---------------------------------------------------------------------------
// Shared, allocation-free primitives used across behaviours.
// ---------------------------------------------------------------------------

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/**
 * Despawn an organism, drop it from its colony's census, and (if a gene store is
 * wired) roll a loot drop. A combat death (`killerId !== NIL`) yields a gene to
 * the KILLER's colony; a natural death (starvation, no killer) salvages one to
 * the victim's OWN colony. Victim colony/caste are read BEFORE despawn.
 */
export function killOrganism(ctx: OrganismCtx, id: EntityId, killerId: EntityId = NIL): void {
  const e = ctx.entities;
  const victimColony = e.colony[id] as number;
  const victimCaste = e.caste[id] as Caste;

  ctx.colonies.onDeath(victimColony, victimCaste);

  // Credit the KILLER's colony with the kill (drives the Predatory personality
  // signal). Read the killer's colony BEFORE any despawn. Additive: the counter
  // is just incremented; a world without the personality layer never reads it.
  if (killerId !== NIL) ctx.colonies.recordKill(e.colony[killerId] as number);

  // Mutation-energy hook: a kill feeds energy to the KILLER's colony. Read the
  // killer's colony BEFORE any despawn. Unwired (no ctx.mutation) ⇒ no-op.
  if (ctx.mutation && killerId !== NIL) {
    ctx.mutation.onKill(e.colony[killerId] as number);
  }

  if (ctx.genes && ctx.geneConfig) {
    const tick = ctx.clock.tick;
    // Event-scaled drop rate: geneDropPct (neutral 100 ⇒ unchanged odds). Exactly
    // ONE rng.int(100) draw is consumed inside the roll regardless of scale.
    const eventPct = ctx.eventMods ? ctx.eventMods.geneDropPct : 100;
    // Per-colony GeneDropRate project bonus multiplies the event scale, crediting the
    // RECIPIENT of the drop — the KILLER's colony on a combat kill, the victim's OWN on
    // a salvage. Neutral 100 (no project / unwired) keeps the exact event threshold, so
    // it's byte-identical; the roll's single rng draw is unchanged regardless of scale.
    const recipient = killerId !== NIL ? (e.colony[killerId] as number) : victimColony;
    const projPct = ctx.projectGeneDropPctOf ? ctx.projectGeneDropPctOf(recipient) : 100;
    const dropScale = projPct === 100 ? eventPct : Math.floor((eventPct * projPct) / 100);
    if (killerId !== NIL) {
      rollCombatDrop(
        ctx.genes,
        ctx.rng,
        e.colony[killerId] as number,
        victimColony,
        victimCaste,
        tick,
        ctx.geneConfig,
        dropScale,
      );
    } else {
      rollDeathDrop(ctx.genes, ctx.rng, victimColony, victimCaste, tick, ctx.geneConfig, dropScale);
    }
  }

  ctx.grid.remove(id);
  e.despawn(id);
}

/**
 * One step toward (tx,ty) using `pathing` (defaults to the ground greedy pathing).
 * Ground movement respects walls + the per-tile cap (via pathing); flying is
 * bounds-only (ignores walls/crowds). Passing `ctx.foragePathing` routes the step
 * AROUND predator danger when the danger-avoidance layer is wired. Returns moved.
 */
function stepOnce(
  ctx: OrganismCtx,
  id: EntityId,
  tx: number,
  ty: number,
  flying: boolean,
  pathing: Pathing = ctx.pathing,
): boolean {
  const e = ctx.entities;
  const ax = e.posX[id] as number;
  const ay = e.posY[id] as number;
  if (ax === tx && ay === ty) return false;
  if (!flying) {
    const dir = pathing.step(ax, ay, tx, ty);
    if (dir === DIR.Stay) return false;
    ctx.grid.move(id, ax + (DIR_DX[dir] as number), ay + (DIR_DY[dir] as number));
    return true;
  }
  // Flying: pick the bounds-admissible neighbour that most reduces distance.
  let bestDir: number = DIR.Stay;
  let bestDist = chebyshev(ax, ay, tx, ty);
  for (let d = 0; d < DIR_COUNT; d++) {
    const nx = ax + (DIR_DX[d] as number);
    const ny = ay + (DIR_DY[d] as number);
    if (!ctx.grid.inBounds(nx, ny)) continue;
    const dist = chebyshev(nx, ny, tx, ty);
    if (dist < bestDist) {
      bestDist = dist;
      bestDir = d;
    }
  }
  if (bestDir === DIR.Stay) return false;
  ctx.grid.move(id, ax + (DIR_DX[bestDir] as number), ay + (DIR_DY[bestDir] as number));
  return true;
}

/**
 * Advance toward (tx,ty) — the gene-granted speed. Two modes, gated on
 * `ctx.creatureSpeed` (the determinism contract):
 *
 *   OFF (absent/false, the default): the ORIGINAL loop — up to integer
 *   `p.stepsPerTick` steps. Untouched, so the world is byte-identical to before.
 *
 *   ON: a fixed-point sub-tick accumulator. Each tick the creature accrues
 *   `p.speed` into `entities.moveAccum[id]`, spends the whole SPEED_UNIT chunks it
 *   has earned as steps THIS tick, and KEEPS only the fractional remainder for next
 *   tick — so slow creatures (speed < SPEED_UNIT) move sub-tile and fast ones move
 *   smoothly. For an INTEGER speed (`p.speed === k * SPEED_UNIT`) moveAccum is always
 *   0 between ticks and exactly k steps fire, identical to the OFF loop with k.
 *
 * Both modes stop early on arrival or when blocked. Blocked/early-arrival steps are
 * LOST (the loop just `break`s) — the accumulator keeps only the sub-tile remainder,
 * never the unspent whole steps. Returns whether it moved at all. The optional
 * `pathing` override routes EVERY step (both modes) through danger-aware pathing for
 * the forage castes (defaults to the ground greedy pathing for everyone else).
 */
function advance(
  ctx: OrganismCtx,
  id: EntityId,
  tx: number,
  ty: number,
  p: CasteProfile,
  pathing: Pathing = ctx.pathing,
): boolean {
  const e = ctx.entities;
  let moved = false;
  if (ctx.creatureSpeed) {
    // Accrue this tick's speed, then compute the whole-step budget; keep the remainder.
    const accrued = (e.moveAccum[id] as number) + p.speed;
    const budget = Math.floor(accrued / SPEED_UNIT);
    e.moveAccum[id] = accrued - budget * SPEED_UNIT; // carry only the fractional part
    for (let s = 0; s < budget; s++) {
      if (!stepOnce(ctx, id, tx, ty, p.flying, pathing)) break;
      moved = true;
      if ((e.posX[id] as number) === tx && (e.posY[id] as number) === ty) break;
    }
    return moved;
  }
  for (let s = 0; s < p.stepsPerTick; s++) {
    if (!stepOnce(ctx, id, tx, ty, p.flying, pathing)) break;
    moved = true;
    if ((e.posX[id] as number) === tx && (e.posY[id] as number) === ty) break;
  }
  return moved;
}

/** One deterministic random step (failed foragers drift). Honours flight. */
function wander(ctx: OrganismCtx, id: EntityId, p: CasteProfile): void {
  const e = ctx.entities;
  const grid = ctx.grid;
  const ax = e.posX[id] as number;
  const ay = e.posY[id] as number;
  const start = ctx.rng.int(DIR_COUNT);
  for (let k = 0; k < DIR_COUNT; k++) {
    const d = (start + k) % DIR_COUNT;
    const nx = ax + (DIR_DX[d] as number);
    const ny = ay + (DIR_DY[d] as number);
    const ok = p.flying ? grid.inBounds(nx, ny) : grid.canMoveAnt(nx, ny);
    if (ok) {
      grid.move(id, nx, ny);
      return;
    }
  }
}

/** Pick a new patrol waypoint within `roam` of home (deterministic via Rng). */
function pickPatrolTarget(ctx: OrganismCtx, id: EntityId, roam: number): void {
  const e = ctx.entities;
  const cid = e.colony[id] as number;
  const hx = ctx.colonies.homeXAt(cid);
  const hy = ctx.colonies.homeYAt(cid);
  const span = 2 * roam + 1;
  e.targetX[id] = clamp(hx + ctx.rng.int(span) - roam, 0, ctx.grid.width - 1);
  e.targetY[id] = clamp(hy + ctx.rng.int(span) - roam, 0, ctx.grid.height - 1);
}

/** Pick a new explore target anywhere on the map (deterministic via Rng). */
function pickExploreTarget(ctx: OrganismCtx, id: EntityId): void {
  const e = ctx.entities;
  e.targetX[id] = ctx.rng.int(ctx.grid.width);
  e.targetY[id] = ctx.rng.int(ctx.grid.height);
}

/**
 * Forager outbound leg: if no cargo, find food and load it (when adjacent),
 * else step toward it / wander. Leaves cargo > 0 once a load is picked up.
 */
function seekAndPickup(ctx: OrganismCtx, id: EntityId, p: CasteProfile): void {
  const e = ctx.entities;
  const ax = e.posX[id] as number;
  const ay = e.posY[id] as number;
  const food = senseNearestFood(e, ctx.grid, ax, ay, p.senseRadius);
  if (food !== NIL) {
    const fx = e.posX[food] as number;
    const fy = e.posY[food] as number;
    if (chebyshev(ax, ay, fx, fy) <= 1) {
      e.cargo[id] = ctx.resources.consume(food, p.carryCapacity);
    } else {
      // Outbound forage leg: route AROUND predator danger when the avoidance layer
      // is wired (foragePathing), else greedy. This is the literal "route change".
      advance(ctx, id, fx, fy, p, ctx.foragePathing ?? ctx.pathing);
    }
  } else {
    wander(ctx, id, p);
  }
}

/**
 * Forager return leg: move toward home; on arrival deposit cargo into the
 * colony store. Returns true on the tick the deposit happens.
 */
function returnAndDeposit(ctx: OrganismCtx, id: EntityId, p: CasteProfile): boolean {
  const e = ctx.entities;
  const cid = e.colony[id] as number;
  const ax = e.posX[id] as number;
  const ay = e.posY[id] as number;
  const hx = ctx.colonies.homeXAt(cid);
  const hy = ctx.colonies.homeYAt(cid);
  if (chebyshev(ax, ay, hx, hy) <= 1) {
    ctx.colonies.depositFood(cid, e.cargo[id] as number);
    e.cargo[id] = 0;
    return true;
  }
  // Return-home leg also skirts predator territory (a laden forager is the most
  // vulnerable). Greedy when no avoidance layer is wired ⇒ byte-identical.
  advance(ctx, id, hx, hy, p, ctx.foragePathing ?? ctx.pathing);
  return false;
}

// ---------------------------------------------------------------------------
// The four caste behaviour templates (stateless singletons).
// ---------------------------------------------------------------------------

const WorkerBehavior: CasteBehavior = {
  caste: CASTE.Worker,
  name: 'worker',
  update(ctx, id) {
    const e = ctx.entities;
    const p = profileOf(ctx, id);
    if ((e.cargo[id] as number) === 0) {
      e.state[id] = ORGANISM_STATE.SeekingFood;
      seekAndPickup(ctx, id, p);
      if ((e.cargo[id] as number) > 0) e.state[id] = ORGANISM_STATE.Returning;
    } else {
      e.state[id] = ORGANISM_STATE.Returning;
      if (returnAndDeposit(ctx, id, p)) e.state[id] = ORGANISM_STATE.SeekingFood;
    }
    e.energy[id] = (e.energy[id] as number) - p.energyDecay;
  },
};

const SoldierBehavior: CasteBehavior = {
  caste: CASTE.Soldier,
  name: 'soldier',
  update(ctx, id) {
    const e = ctx.entities;
    const p = profileOf(ctx, id);
    const ax = e.posX[id] as number;
    const ay = e.posY[id] as number;
    const enemy = senseNearestEnemy(e, ctx.grid, ax, ay, p.senseRadius, e.colony[id] as number);
    if (enemy !== NIL) {
      e.state[id] = ORGANISM_STATE.Attacking;
      const ex = e.posX[enemy] as number;
      const ey = e.posY[enemy] as number;
      if (chebyshev(ax, ay, ex, ey) <= 1) {
        const hp = (e.hp[enemy] as number) - p.attackPower;
        e.hp[enemy] = hp;
        if (hp <= 0) killOrganism(ctx, enemy, id);
      } else {
        advance(ctx, id, ex, ey, p);
      }
    } else if (defendAgainstWildlife(ctx, id, p, ax, ay)) {
      // Engaged a non-colony predator inside our patrol zone (handled in-helper).
    } else {
      e.state[id] = ORGANISM_STATE.Patrolling;
      if (chebyshev(ax, ay, e.targetX[id] as number, e.targetY[id] as number) === 0) {
        pickPatrolTarget(ctx, id, p.roamRadius);
      }
      const moved = advance(ctx, id, e.targetX[id] as number, e.targetY[id] as number, p);
      if (!moved) pickPatrolTarget(ctx, id, p.roamRadius);
    }
    e.energy[id] = (e.energy[id] as number) - p.energyDecay;
  },
};

/**
 * Colony DEFENCE against non-colony wildlife (the two-way half of the ecosystem
 * interaction). A Soldier with no rival-colony enemy strikes adjacent wildlife,
 * or closes on a predator that has entered its patrol leash (Chebyshev roamRadius
 * of home) — but never chases beyond it (so a fast Bird can't lure soldiers off
 * the nest). Damage only: the wildlife layer owns the kill + census cleanup at
 * hp<=0. Returns true if it engaged (so the Soldier skips its patrol step).
 * In a wildlife-free world senseNearestWildlife is NIL ⇒ returns false ⇒ the
 * Soldier patrols exactly as before (colony determinism is untouched).
 */
function defendAgainstWildlife(
  ctx: OrganismCtx,
  id: EntityId,
  p: CasteProfile,
  ax: number,
  ay: number,
): boolean {
  const e = ctx.entities;
  const threat = senseNearestWildlife(e, ctx.grid, ax, ay, p.senseRadius);
  if (threat === NIL) return false;
  const tx = e.posX[threat] as number;
  const ty = e.posY[threat] as number;
  if (chebyshev(ax, ay, tx, ty) <= 1) {
    e.state[id] = ORGANISM_STATE.Attacking;
    e.hp[threat] = (e.hp[threat] as number) - p.attackPower;
    return true;
  }
  // Only pursue a predator that is inside this Soldier's patrol leash.
  const cid = e.colony[id] as number;
  if (chebyshev(ctx.colonies.homeXAt(cid), ctx.colonies.homeYAt(cid), tx, ty) <= p.roamRadius) {
    e.state[id] = ORGANISM_STATE.Attacking;
    advance(ctx, id, tx, ty, p);
    return true;
  }
  return false;
}

const FlyerBehavior: CasteBehavior = {
  caste: CASTE.Flyer,
  name: 'flyer',
  update(ctx, id) {
    const e = ctx.entities;
    const p = profileOf(ctx, id);
    const cid = e.colony[id] as number;
    e.state[id] = ORGANISM_STATE.Exploring;
    // Below a quarter energy, head home to refuel (nest-feeding tops it up).
    if ((e.energy[id] as number) * 4 <= p.startEnergy) {
      const hx = ctx.colonies.homeXAt(cid);
      const hy = ctx.colonies.homeYAt(cid);
      e.targetX[id] = hx;
      e.targetY[id] = hy;
      advance(ctx, id, hx, hy, p);
    } else {
      const ax = e.posX[id] as number;
      const ay = e.posY[id] as number;
      if (chebyshev(ax, ay, e.targetX[id] as number, e.targetY[id] as number) === 0) {
        pickExploreTarget(ctx, id);
      }
      const moved = advance(ctx, id, e.targetX[id] as number, e.targetY[id] as number, p);
      if (!moved) pickExploreTarget(ctx, id);
    }
    e.energy[id] = (e.energy[id] as number) - p.energyDecay;
  },
};

const SpecialistBehavior: CasteBehavior = {
  caste: CASTE.Specialist,
  name: 'specialist',
  update(ctx, id) {
    const e = ctx.entities;
    const p = profileOf(ctx, id);
    const cid = e.colony[id] as number;
    const lowFood = ctx.colonies.lowFoodThreshold;

    if ((e.state[id] as number) === ORGANISM_STATE.Idle) {
      // Trigger: colony food stressed → wake as an emergency forager.
      if (ctx.colonies.foodAt(cid) < lowFood) {
        e.state[id] = ORGANISM_STATE.SeekingFood;
      } else {
        return; // dormant: no movement, no energy spent
      }
    }

    // Active: forage like a Worker, but stand down (idle) on a home delivery
    // once the colony has recovered.
    if ((e.cargo[id] as number) === 0) {
      e.state[id] = ORGANISM_STATE.SeekingFood;
      seekAndPickup(ctx, id, p);
      if ((e.cargo[id] as number) > 0) e.state[id] = ORGANISM_STATE.Returning;
    } else {
      e.state[id] = ORGANISM_STATE.Returning;
      if (returnAndDeposit(ctx, id, p)) {
        e.state[id] =
          ctx.colonies.foodAt(cid) >= lowFood
            ? ORGANISM_STATE.Idle
            : ORGANISM_STATE.SeekingFood;
      }
    }
    e.energy[id] = (e.energy[id] as number) - p.energyDecay;
  },
};

/**
 * Caste-indexed dispatch table. Order MUST match the CASTE.* values so
 * CASTE_BEHAVIORS[caste] is the right template. Shared by all organisms.
 */
export const CASTE_BEHAVIORS: readonly CasteBehavior[] = [
  WorkerBehavior, // CASTE.Worker  = 0
  SoldierBehavior, // CASTE.Soldier = 1
  FlyerBehavior, // CASTE.Flyer   = 2
  SpecialistBehavior, // CASTE.Specialist = 3
];
